/**
 * Credenciales del PMIS: capa de autenticación a nivel de aplicación,
 * independiente del proveedor subyacente (SharePoint / MSAL / Dexie local).
 *
 * **Propósito**: que el ADMIN_PMIS pueda imponer una segunda barrera de
 * acceso al PMIS sin depender de la infraestructura de IT del sitio donde
 * se instale (Tecpetrol, otra empresa, otro tenant). Si SP/MSAL cambian
 * sus reglas, el PMIS mantiene su propia matriz de claves intacta.
 *
 * **Modelo de datos**:
 *   - Claves se guardan en `localStorage["pmis_local_creds"]` como
 *     `{ email → { salt, hash, actualizadaAt, actualizadaPor } }`.
 *   - El hash es SHA-256(salt + ":: " + password). No es bcrypt/argon2,
 *     pero es suficiente para un piloto local que no expone hashes en
 *     red. Si en un futuro se migra a SP, las claves se reemiten o se
 *     reescribe esta capa contra `Mail.Send` / lista SP.
 *   - localStorage tiene prefijo `pmis_*` para que entre en el backup
 *     completo del módulo Admin → Backup/Restore.
 *
 * **Reglas**:
 *   - El ADMIN_PMIS pasa sin clave (su rol gobierna el sistema, debe
 *     poder entrar a soporte aún si la base está rota).
 *   - El resto de usuarios entran con la **clave canónica del piloto**
 *     (`CLAVE_DEFAULT_PILOTO`) hasta que el admin les fije una propia.
 *     Esto evita bloquear al equipo durante el roll-out.
 *   - Solo el ADMIN_PMIS puede llamar `establecerClave` desde la UI
 *     (el guard lo aplica el componente que llama, esta capa es pura).
 */

import { cargarMapaCredenciales, guardarMapaCredenciales } from "./credencialesStore";
import { DEPLOYMENT } from "@core/access/deployment";

const STORAGE_MODO_ACCESO = "pmis_modo_acceso";
const STORAGE_CLAVE_ADMIN = "pmis_clave_admin_config";

/**
 * Modo ENDURECIDO para producción (build de Tecpetrol con `VITE_HARDEN_AUTH=1`).
 * Cuando está activo: las 3 claves de prueba se ELIMINAN del bundle, se fuerza
 * el modo por-usuario (sp-tenant), se desactivan la clave maestra y el rescate,
 * y la clave admin inicial sale de `VITE_CLAVE_ADMIN_INICIAL` (configurable por
 * despliegue, no hardcodeada). El piloto de Cloudflare NO lo activa → su flujo
 * de pruebas multi-rol no cambia.
 */
export const HARDEN_AUTH = DEPLOYMENT.hardened;

// Caché en memoria del mapa de credenciales. Se hidrata desde el backend
// (Supabase compartido o localStorage) vía `hydrateCredenciales()` al iniciar
// el login. Las lecturas síncronas (tieneClave, leerRegistroCredencial) usan
// este caché; las escrituras lo actualizan y persisten al backend.
let _mapaCache: Record<string, RegistroCredencial> = {};

/**
 * Carga el mapa de credenciales desde el backend al caché en memoria.
 * Debe llamarse al montar el proveedor de login (antes de leer credenciales).
 */
export async function hydrateCredenciales(): Promise<void> {
  try {
    _mapaCache = await cargarMapaCredenciales();
  } catch (e) {
    console.warn("[credenciales] hidratación falló, uso caché vacío:", e);
    _mapaCache = {};
  }
}

// NOTA DE SEGURIDAD / AUDITORÍA: ninguna clave se hardcodea en el código fuente.
// Las claves de conveniencia del piloto se inyectan por entorno (`.env.<perfil>.local`,
// fuera del repo) y NO existen en el build endurecido. Así el código que se
// entrega a auditoría no contiene ningún secreto real.

/**
 * Clave de rescate del modo `sp-tenant` para usuarios que aún no tienen
 * clave personal asignada — evita dejar al equipo bloqueado durante el
 * bootstrap del PMIS. En modo `clave-maestra` se usa `CLAVE_MAESTRA_PRUEBAS`.
 * Valor del piloto: `VITE_CLAVE_PILOTO` (env). Endurecido / sin env: "".
 */
export const CLAVE_DEFAULT_PILOTO =
  HARDEN_AUTH ? "" : (import.meta.env.VITE_CLAVE_PILOTO ?? "");

/**
 * Clave maestra del modo PRUEBAS: cualquier usuario (excepto el ADMIN_PMIS)
 * puede entrar con esta clave a CUALQUIER cuenta para hacer pruebas de
 * funcionamiento en vivo (impersonación libre para testing multi-rol).
 * NO usar en producción. Valor del piloto: `VITE_CLAVE_MAESTRA` (env).
 */
export const CLAVE_MAESTRA_PRUEBAS =
  HARDEN_AUTH ? "" : (import.meta.env.VITE_CLAVE_MAESTRA ?? "");

/**
 * Clave INICIAL de configuración del Administrador del PMIS. Es la segunda
 * barrera independiente del SP que protege las funciones críticas del
 * admin (reset, backup, gestión de claves, modo de acceso). Solo el admin
 * la conoce. Cambiable por el propio admin desde Inicio. Valor por despliegue:
 * `VITE_CLAVE_ADMIN_INICIAL` (env). Sin env: "" (no se acepta clave inicial).
 */
export const CLAVE_ADMIN_INICIAL = import.meta.env.VITE_CLAVE_ADMIN_INICIAL ?? "";

/**
 * Modos de acceso al PMIS:
 *
 *  - `"clave-maestra"`: PRUEBAS. Cualquier usuario no-admin entra con la
 *    `CLAVE_MAESTRA_PRUEBAS` a cualquier email. Útil para probar todos los
 *    roles sin gestionar credenciales individuales.
 *  - `"sp-tenant"`: PRODUCCIÓN. Cada usuario entra con la clave personal
 *    que el admin le asignó en el módulo Autoridades. Sin fallback común.
 *    Los externos (contratistas, interventores) reciben su clave del admin.
 *    El admin entra con su propia clave personal. La clave de SP
 *    corporativo, cuando exista, será una barrera anterior gestionada por
 *    el navegador / MSAL.
 */
export type ModoAcceso = "clave-maestra" | "sp-tenant";

export function leerModoAcceso(): ModoAcceso {
  // Modo endurecido (producción): siempre por-usuario, sin clave maestra.
  if (HARDEN_AUTH) return "sp-tenant";
  try {
    const raw = localStorage.getItem(STORAGE_MODO_ACCESO);
    return raw === "sp-tenant" ? "sp-tenant" : "clave-maestra";
  } catch {
    return "clave-maestra";
  }
}

export function establecerModoAcceso(modo: ModoAcceso): void {
  localStorage.setItem(STORAGE_MODO_ACCESO, modo);
}

export interface RegistroCredencial {
  /** Salt aleatorio (hex) generado al crear la clave. */
  salt: string;
  /** Hash de la clave (PBKDF2 si `alg` presente; SHA-256 legado si no). */
  hash: string;
  /** KDF usado. Ausente = legado SHA-256; `pbkdf2-sha256` = PBKDF2. */
  alg?: typeof PBKDF2_ALG;
  /** Iteraciones PBKDF2 (solo cuando `alg` = pbkdf2-sha256). */
  iter?: number;
  /** ISO de la última vez que se creó/actualizó la clave. */
  actualizadaAt: string;
  /** Email del admin que aplicó el cambio — para trazabilidad. */
  actualizadaPor?: string;
}

// ── Almacenamiento (localStorage por ahora) ─────────────────────────────

/** Lectura síncrona del mapa de credenciales (caché hidratado al iniciar). */
function leerMapa(): Record<string, RegistroCredencial> {
  return _mapaCache;
}

/** Actualiza el caché y persiste al backend (Supabase compartido o local). */
async function guardarMapa(mapa: Record<string, RegistroCredencial>): Promise<void> {
  _mapaCache = mapa;
  await guardarMapaCredenciales(mapa);
}

// ── Hashing ─────────────────────────────────────────────────────────────
//
// El hashing usa PBKDF2-SHA256 (Web Crypto, sin dependencias nuevas) con
// factor de costo, en vez del SHA-256 de una sola ronda anterior (trivial de
// fuerza-brutar offline). Cada registro guarda `alg`/`iter`. Los registros
// legados (sin `alg`) se validan con el esquema viejo y se re-hashean a PBKDF2
// de forma transparente la próxima vez que el usuario entra (migración perezosa
// en `verificarClave` / `verificarClaveAdmin`).

const PBKDF2_ALG = "pbkdf2-sha256" as const;
const PBKDF2_ITER = 200_000;

function bytesAHex(arr: Uint8Array): string {
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return bytesAHex(new Uint8Array(buf));
}

async function pbkdf2Hex(password: string, salt: string, iter: number): Promise<string> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: enc.encode(salt), iterations: iter, hash: "SHA-256" },
    keyMaterial,
    256,
  );
  return bytesAHex(new Uint8Array(bits));
}

function generarSalt(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return bytesAHex(arr);
}

/** Campos de hashing comunes a credenciales de usuario y a la clave admin. */
interface HashFields {
  salt: string;
  hash: string;
  /** KDF usado. Ausente = legado SHA-256 (1 ronda); `pbkdf2-sha256` = PBKDF2. */
  alg?: typeof PBKDF2_ALG;
  /** Iteraciones PBKDF2 (solo cuando `alg` = pbkdf2-sha256). */
  iter?: number;
}

/** Crea los campos de hash (PBKDF2) para una clave nueva. Exportada para pruebas. */
export async function crearHash(password: string): Promise<HashFields> {
  const salt = generarSalt();
  const hash = await pbkdf2Hex(password, salt, PBKDF2_ITER);
  return { salt, hash, alg: PBKDF2_ALG, iter: PBKDF2_ITER };
}

/**
 * Verifica `password` contra un registro de hash. Soporta PBKDF2 (con `alg`) y
 * el esquema legado SHA-256 de una ronda (sin `alg`). Exportada para pruebas.
 */
export async function verificarContraRegistro(
  password: string,
  registro: HashFields,
): Promise<boolean> {
  if (registro.alg === PBKDF2_ALG) {
    const intento = await pbkdf2Hex(password, registro.salt, registro.iter ?? PBKDF2_ITER);
    return intento === registro.hash;
  }
  const intento = await sha256Hex(`${registro.salt}:: ${password}`);
  return intento === registro.hash;
}

/** ¿El registro usa el esquema legado (conviene re-hashearlo a PBKDF2)? */
function esHashLegado(registro: HashFields): boolean {
  return registro.alg !== PBKDF2_ALG;
}

// ── API pública ─────────────────────────────────────────────────────────

/** ¿El usuario tiene una clave establecida? */
export function tieneClave(email: string): boolean {
  const mapa = leerMapa();
  return mapa[email.toLowerCase()] !== undefined;
}

/** Lee el registro (sin el hash visible — útil solo para metadata). */
export function leerRegistroCredencial(email: string): RegistroCredencial | null {
  const mapa = leerMapa();
  return mapa[email.toLowerCase()] ?? null;
}

/**
 * Establece la clave de un usuario. Si ya existía, la reemplaza. El caller
 * debe garantizar la autorización (típicamente ADMIN_PMIS o el propio
 * usuario cambiando su clave).
 *
 * @param email del usuario al que se le fija la clave
 * @param password texto plano de la nueva clave (mínimo 4 caracteres)
 * @param actorEmail email del admin/operador que aplica el cambio
 */
export async function establecerClave(
  email: string,
  password: string,
  actorEmail?: string,
): Promise<void> {
  if (!password || password.length < 4) {
    throw new Error("La clave debe tener al menos 4 caracteres.");
  }
  const campos = await crearHash(password);
  const mapa = leerMapa();
  mapa[email.toLowerCase()] = {
    ...campos,
    actualizadaAt: new Date().toISOString(),
    ...(actorEmail ? { actualizadaPor: actorEmail } : {}),
  };
  await guardarMapa(mapa);
}

/** Borra la clave de un usuario — vuelve al estado "sin clave propia". */
export async function eliminarClave(email: string): Promise<void> {
  const mapa = leerMapa();
  delete mapa[email.toLowerCase()];
  await guardarMapa(mapa);
}

/**
 * Verifica una clave de usuario sin contemplar el modo de acceso. La usa
 * el provider de login después de elegir la política según modo. Retorna:
 *   - `"ok"`: la clave coincide con el hash guardado para ese usuario.
 *   - `"default"`: el usuario no tiene clave propia pero la provista es
 *     la del piloto (`CLAVE_DEFAULT_PILOTO`). Solo válida si el caller
 *     decide aceptar fallback.
 *   - `"nope"`: clave incorrecta.
 */
export async function verificarClave(
  email: string,
  password: string,
): Promise<"ok" | "default" | "nope"> {
  // Refresca desde el backend para ver claves recién fijadas/restablecidas en
  // otro equipo (en supabase el mapa es compartido).
  await hydrateCredenciales();
  const registro = leerRegistroCredencial(email);
  if (registro) {
    const ok = await verificarContraRegistro(password, registro);
    if (ok && esHashLegado(registro)) {
      // Migración perezosa a PBKDF2 (preserva metadata del registro).
      const mapa = leerMapa();
      mapa[email.toLowerCase()] = { ...registro, ...(await crearHash(password)) };
      await guardarMapa(mapa);
    }
    return ok ? "ok" : "nope";
  }
  return password === CLAVE_DEFAULT_PILOTO ? "default" : "nope";
}

// ── Clave de configuración del ADMIN_PMIS ───────────────────────────────
//
// Segunda barrera totalmente independiente del SP/MSAL. Protege las
// "funciones de configuración" del admin (reset, backup, gestión de
// claves, modo de acceso). El admin la cambia a voluntad. Si nunca se
// estableció, se usa `CLAVE_ADMIN_INICIAL` como fallback.

interface RegistroClaveAdmin {
  salt: string;
  hash: string;
  alg?: typeof PBKDF2_ALG;
  iter?: number;
  actualizadaAt: string;
}

function leerRegistroClaveAdmin(): RegistroClaveAdmin | null {
  try {
    const raw = localStorage.getItem(STORAGE_CLAVE_ADMIN);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RegistroClaveAdmin;
    if (
      typeof parsed.salt !== "string" ||
      typeof parsed.hash !== "string" ||
      typeof parsed.actualizadaAt !== "string"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Establece (o cambia) la clave de configuración del admin. */
export async function establecerClaveAdmin(nuevaClave: string): Promise<void> {
  if (!nuevaClave || nuevaClave.length < 4) {
    throw new Error("La clave de configuración debe tener al menos 4 caracteres.");
  }
  const registro: RegistroClaveAdmin = {
    ...(await crearHash(nuevaClave)),
    actualizadaAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_CLAVE_ADMIN, JSON.stringify(registro));
}

/** ¿El admin ya cambió la clave inicial? */
export function tieneClaveAdminPropia(): boolean {
  return leerRegistroClaveAdmin() !== null;
}

/**
 * Verifica la clave de configuración del admin. Si nunca se estableció,
 * acepta `CLAVE_ADMIN_INICIAL`. Si ya se estableció, solo acepta el hash
 * guardado — la clave inicial deja de funcionar.
 */
export async function verificarClaveAdmin(intento: string): Promise<boolean> {
  const registro = leerRegistroClaveAdmin();
  if (!registro) {
    // Solo vale la clave inicial si está configurada (nunca vacía: evita aceptar
    // entrada en blanco en modo endurecido sin clave admin provisionada).
    return CLAVE_ADMIN_INICIAL !== "" && intento === CLAVE_ADMIN_INICIAL;
  }
  const ok = await verificarContraRegistro(intento, registro);
  if (ok && esHashLegado(registro)) {
    // Migración perezosa a PBKDF2.
    const migrado: RegistroClaveAdmin = { ...registro, ...(await crearHash(intento)) };
    localStorage.setItem(STORAGE_CLAVE_ADMIN, JSON.stringify(migrado));
  }
  return ok;
}


/** Fecha ISO de la última actualización, o null si nunca se cambió. */
export function fechaUltimaClaveAdmin(): string | null {
  return leerRegistroClaveAdmin()?.actualizadaAt ?? null;
}
